import { useTranslation } from 'react-i18next';
import { Button, cn } from '@scio3/ui';
import { type PageSuggestion } from '../utils/page-suggestions';

interface OptiBotSuggestionsProps {
  suggestions: PageSuggestion[];
  onSuggestionClick: (question: string) => void;
  /** Center the label and pills (used by the welcome hero empty state). */
  centered?: boolean;
  /** Stack pills vertically (on top of each other) instead of wrapping in a row. */
  stacked?: boolean;
  className?: string;
}

/**
 * Contextual suggestion pills shown below the welcome message.
 * Each pill sends a pre-defined question to OptiBot when clicked.
 * Rendered as compact, rounded pills — wrapping in a row, or stacked vertically.
 */
export function OptiBotSuggestions({
  suggestions,
  onSuggestionClick,
  centered = false,
  stacked = false,
  className,
}: OptiBotSuggestionsProps) {
  const { t } = useTranslation();

  if (suggestions.length === 0) return null;

  return (
    <div className={cn('space-y-1.5', className)}>
      <p className={cn('text-xs font-medium text-muted-foreground', centered && 'text-center')}>
        {t('optibot.commonQuestions')}
      </p>
      <div
        className={cn(
          'flex gap-2',
          stacked ? 'flex-col items-start' : 'flex-wrap',
          centered && (stacked ? 'items-center' : 'justify-center'),
        )}
      >
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion.labelKey}
            variant="outline"
            size="sm"
            onClick={() => onSuggestionClick(suggestion.question)}
            className="rounded-full px-3 py-1.5 text-xs"
          >
            {t(suggestion.labelKey)}
          </Button>
        ))}
      </div>
    </div>
  );
}
